import React, { useEffect } from 'react';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMultiWalletState, MultiWalletContext } from '../hooks/useMultiWallet';
import WalletBrowserDrawer from '../components/WalletBrowserDrawer';
import WalletLinkHandoff from '../components/WalletLinkHandoff';
import WalletDiagnostics from '../components/WalletDiagnostics';

/** Диагностика кошельков открывается по ?wtdiag=1 — с телефона это единственный
 * способ увидеть, на каком шаге рвётся подключение. */
function diagnosticsRequested(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('wtdiag') === '1';
  } catch {
    return false;
  }
}

/**
 * Один экземпляр состояния кошельков на всё приложение. Монтируется в index.tsx
 * только вне Telegram: в мини-аппе этот слой не нужен, и там useMultiWallet()
 * отдаёт инертное значение с enabled = false.
 *
 * Здесь же живёт окно выбора кошелька для открытия страницы в его встроенном
 * браузере: вызвать его может любой экран, а показывать его нужно поверх всего.
 * И экран привязки — обратная сторона того же перехода: он показывается уже
 * внутри браузера кошелька, поверх любого экрана, на который человек попал по
 * ссылке.
 */
/**
 * Причина неудавшейся привязки — на экран, а не в консоль.
 *
 * Это тот самый разрыв, из-за которого «подключил кошелёк, а в приложении
 * пусто» выглядело необъяснимо: ошибка привязки ловилась и уходила в
 * console.warn, интерфейс не менялся, и человек оставался с рабочим на вид
 * подключением и пустым балансом.
 */
function useLinkNotice(notice: string | null, dismiss: () => void): void {
  const { t } = useTranslation();
  useEffect(() => {
    if (!notice) return;
    const text =
      notice === 'phantom_connect_failed'
        ? t('Phantom did not return a connection. Open it again and confirm the request.')
        : notice === 'link_already_linked'
        ? t('This wallet is already linked to another account.')
        : notice === 'link_unauthorized' || notice === 'link_expired'
        ? t('Sign in to KredaFi again and reconnect the wallet — the link session has expired.')
        : notice === 'link_invalid_address'
        ? t('The wallet returned an address this network does not accept.')
        : t('The wallet is connected, but linking it to your account failed. The balance will not show up in the app until it is linked.');
    message.error(text);
    dismiss();
  }, [notice, dismiss, t]);
}

export function MultiWalletProvider({ children }: { children: React.ReactNode }) {
  const state = useMultiWalletState();
  useLinkNotice(state.linkNotice, state.dismissLinkNotice);
  return (
    <MultiWalletContext.Provider value={state}>
      {children}
      <WalletBrowserDrawer
        open={state.walletPickerFamily !== null}
        family={state.walletPickerFamily}
        onClose={state.closeWalletPicker}
      />
      <WalletLinkHandoff state={state.linkHandoff} />
      {diagnosticsRequested() && <WalletDiagnostics />}
    </MultiWalletContext.Provider>
  );
}
